// hooks
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';

// utils
import {
  addDays,
  differenceInCalendarDays,
  endOfDay,
  isAfter,
  isBefore,
  isSameDay,
  startOfDay
} from 'date-fns';
import {
  parseBackendDateTime,
  suffixZ,
  toBackendDateTime,
  toDateString
} from 'utils/date';
import {
  createAbsenceUpdateDuties,
  getAbsenceById,
  getAbsenceRollout,
  getRegisterAbsenceDuties,
  getRegisterAbsenceDutyHourTypes,
  updateAbsence
} from 'RestAPIs/roster/register-absence';
import { showSuccessSnackbar } from 'components/shared/Snackbar';
import showError from 'components/shared/showError';
import { queries as rapiniQueries } from 'RestAPIs/rapini';
import { useWorkingDaysSettings } from './useWorkingDaysSettings';

// types
import type {
  AbsenceRolloutDutyLine,
  PublicDutyHourType,
  RegisterAbsenceWorkAgreement
} from 'RestAPIs/roster/register-absence';
import type { RegisterAbsenceDialogEmployee } from './RegisterAbsenceDialog.types';
import type { DateChangeOrigin } from 'components/shared/DatePicker/DatePicker.types';

// Absences registered from the notice flows are sickness reports, so every
// duty hour type on dcc 5 (ziekte, geen ORT) is offered — ZK, ZKZ, but also
// e.g. reintegratie and zwangerschapsverlof. Widened from only ZK/ZKZ on
// 8 aug 2026: the dcc is the definition of "sickness", not the two codes.
const SICKNESS_DCC_SVC = 5;

// ziekte (ZK) is the default choice, so it sorts first
const DEFAULT_DUTY_HOUR_TYPE_CODE = 'ZK';

// The aanvraagreden is always one of two codes and follows directly from the
// chosen duty hour type: ziekte → ZK, ziekte t.g.v. zwangerschap → ZW
const REASON_CODE_BY_DUTY_HOUR_TYPE_CODE: Record<string, string> = {
  ZK: 'ZK',
  ZKZ: 'ZW'
};

const ROLLOUT_DEBOUNCE_MS = 400;

export const WEEKDAYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday'
] as const;

export type Weekday = (typeof WEEKDAYS)[number];

/** Per work agreement: how new (sickness) duties are rolled out when the
 *  employee has no planned duties — the planner's decision. The rollout is an
 *  explicit opt-in; by default only the planned duties are converted. */
export type WorkAgreementSetting = {
  useFteRollout: boolean;
  /** the planner (un)checked the rollout: stop choosing for them */
  useFteRolloutTouched?: boolean;
  rolloutStartDate: Date | null;
  /** the planner set the rollout date by hand: stop following the period */
  rolloutStartDateTouched?: boolean;
  inputDays: Weekday[];
  inputWeekHours: number;
};

/** A zero-hours contract (NUC) carries no week hours of its own: the planner
 *  enters them per absence, so nothing about it can be filled in up front. */
export const isZeroHoursContract = (
  workAgreement: RegisterAbsenceWorkAgreement
) => workAgreement.contractCode === '0';

/** PNIL ('tmp') is paid through the external organisation: the absence itself
 *  can be registered (the roster must be emptied), but no sickness duties are
 *  ever created, so the rollout stays off. The backend refuses it as well. */
export const isPnilContract = (workAgreement: RegisterAbsenceWorkAgreement) =>
  workAgreement.contractCode === 'tmp';

/** Defaults: rollout is off; when the planner opts in, duties are created from
 *  the first day of the absence, so the whole period is covered. The
 *  checkboxes are the recorded working days, else the contract day flags. */
const createDefaultSetting = (
  workAgreement: RegisterAbsenceWorkAgreement,
  fallbackRolloutStartDate: Date | null,
  recorded?: { days: Weekday[] }
): WorkAgreementSetting => ({
  useFteRollout: false,
  rolloutStartDate:
    fallbackRolloutStartDate ??
    (workAgreement.fteStartDate
      ? parseBackendDateTime(workAgreement.fteStartDate)
      : null),
  // the planner's recorded working days win over the (often empty) contract
  // day flags
  inputDays:
    recorded?.days ??
    WEEKDAYS.filter((day) => workAgreement[`${day}Hours`] === true),
  // deliberately NOT the recorded hours: on a zero-hours contract the hours
  // differ per absence, so carrying the previous ones over would quietly book
  // the wrong number. Only the days are worth remembering; the hours come
  // from the work agreement (zero on a zero-hours contract) and are entered
  // per sickness notice.
  inputWeekHours: workAgreement.hoursPerWeek ?? workAgreement.weekHoursMin ?? 0
});

/** the duties endpoint returns one row per benefit, so a work agreement whose
 *  inschaling changes inside the period comes back more than once */
const firstRowPerWorkAgreement = (rows: RegisterAbsenceWorkAgreement[]) => {
  const firstByWaId = new Map<number, RegisterAbsenceWorkAgreement>();
  for (const row of rows) {
    if (!firstByWaId.has(row.waId)) {
      firstByWaId.set(row.waId, row);
    }
  }
  return [...firstByWaId.values()];
};

/** the inschaling changes inside the period when the benefit that the endpoint
 *  returned does not span it (MyBatis collapses the rows per work agreement, so
 *  the validity of that one benefit is the only signal) */
const getBenefitChangeByWaId = (
  rows: RegisterAbsenceWorkAgreement[],
  startDate: Date | null,
  endDate: Date | null
) => {
  const changes: Record<number, Date> = {};
  if (!startDate || !endDate) {
    return changes;
  }
  for (const row of rows) {
    const validTo = row.validTo ? parseBackendDateTime(row.validTo) : null;
    const validFrom = row.validFrom
      ? parseBackendDateTime(row.validFrom)
      : null;
    if (
      validTo &&
      isAfter(validTo, startDate) &&
      isBefore(validTo, endOfDay(endDate))
    ) {
      changes[row.waId] = validTo;
    } else if (validFrom && isAfter(validFrom, startDate)) {
      changes[row.waId] = validFrom;
    }
  }
  return changes;
};

/** duties-in-period per work agreement, as decision info for the choice */
const countDutiesByWaId = (lines: AbsenceRolloutDutyLine[]) => {
  const counts: Record<number, number> = {};
  for (const line of lines) {
    if (line.waId) {
      counts[line.waId] = (counts[line.waId] ?? 0) + 1;
    }
  }
  return counts;
};

/**
 * Edit mode does not call the rollout: the consequences follow from the linked
 * duties and the backend's own rules, computed here. A duty that falls
 * completely outside the shrunk period is deleted; the remaining duties convert
 * when the duty hour type changed and stay as they are otherwise.
 */
const toEditDutyLines = ({
  duties,
  originalTypeDescription,
  startDate,
  endDate,
  typeChanged
}: {
  duties: AbsenceRolloutDutyLine[];
  originalTypeDescription: string;
  startDate: Date | null;
  endDate: Date | null;
  typeChanged: boolean;
}) => {
  // sickness duty lines carry no duty code, so the duty column shows the
  // absence's own duty hour type ("Ziekte") instead of staying empty
  const withType = duties.map((line) => ({
    ...line,
    displayDutyCode:
      line.displayDutyCode || line.dutyCode || originalTypeDescription
  }));
  if (!startDate || !endDate) {
    return withType;
  }
  const periodStart = startOfDay(startDate);
  const periodEnd = endOfDay(endDate);

  return withType.map((line) => {
    const outsidePeriod =
      isBefore(parseBackendDateTime(line.endTime), periodStart) ||
      isAfter(parseBackendDateTime(line.startTime), periodEnd);
    return {
      ...line,
      action: outsidePeriod ? 'DELETE' : typeChanged ? 'MODIFY' : 'SKIP'
    };
  });
};

type UseRegisterAbsenceDialogArgs = {
  open: boolean;
  employee: RegisterAbsenceDialogEmployee;
  /** Prefilled period, e.g. from the absence notice being processed */
  initialStartDate?: Date;
  initialEndDate?: Date;
  /** editing an existing absence: loads it and switches the dialog to edit
   *  mode (period can only shrink, no rollout, PUT on save) */
  absenceId?: number;
  onClose: () => void;
  onSuccess?: () => void;
};

export const useRegisterAbsenceDialog = ({
  open,
  employee,
  initialStartDate,
  initialEndDate,
  absenceId,
  onClose,
  onSuccess
}: UseRegisterAbsenceDialogArgs) => {
  // NOTE: deliberately no use-memo directive here, so the useMemo calls below
  // stay. The React Compiler skips this hook entirely (`npm run
  // react-compiler:check` reports InvalidReact) because of the deliberate
  // react-hooks/exhaustive-deps suppressions below, so annotating it would drop
  // the memoization instead of taking it over. Splitting the hook (#2399)
  // removes those suppressions; the directive belongs there.
  const isEdit = absenceId !== undefined;
  const today = useMemo(() => startOfDay(new Date()), []);
  const [startDate, setStartDate] = useState<Date | null>(
    initialStartDate ?? today
  );
  const [endDate, setEndDate] = useState<Date | null>(
    initialEndDate ?? initialStartDate ?? today
  );
  const [dutyTypeId, setDutyTypeId] = useState<number | ''>('');
  const [percentage, setPercentage] = useState(100);
  const [note, setNote] = useState('');
  const [selectedWaIds, setSelectedWaIds] = useState<number[]>([]);
  const [workAgreementSettings, setWorkAgreementSettings] = useState<
    Record<number, WorkAgreementSetting>
  >({});

  const seenWaIdsRef = useRef<Set<number>>(new Set());
  const { getSettings, saveSettings } = useWorkingDaysSettings();

  // reset the form every time the dialog opens for a (new) employee
  useEffect(() => {
    if (open) {
      setStartDate(initialStartDate ?? today);
      setEndDate(initialEndDate ?? initialStartDate ?? today);
      setNote('');
      setPercentage(100);
      setSelectedWaIds([]);
      setWorkAgreementSettings({});
      seenWaIdsRef.current = new Set();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, employee.id, absenceId]);

  // edit mode: the absence record, its work agreement and its linked duties
  // in one request
  const absenceDetailsQuery = useQuery(
    ['registerAbsenceDetails', absenceId],
    async () => (await getAbsenceById(absenceId!)).data,
    { enabled: open && isEdit }
  );
  const originalAbsence = absenceDetailsQuery.data?.absence;
  const originalStartDate = useMemo(
    () =>
      originalAbsence
        ? startOfDay(parseBackendDateTime(originalAbsence.startTime))
        : null,
    [originalAbsence]
  );
  const originalEndDate = useMemo(
    () =>
      originalAbsence
        ? startOfDay(parseBackendDateTime(originalAbsence.endTime))
        : null,
    [originalAbsence]
  );

  // populate the form from the loaded absence (once per open)
  useEffect(() => {
    if (!open || !isEdit || !originalAbsence) {
      return;
    }
    setStartDate(startOfDay(parseBackendDateTime(originalAbsence.startTime)));
    setEndDate(startOfDay(parseBackendDateTime(originalAbsence.endTime)));
    setDutyTypeId(originalAbsence.dutyTypeId);
    setPercentage(originalAbsence.absPercentage ?? 100);
    setNote(originalAbsence.note ?? '');
  }, [open, isEdit, originalAbsence]);

  // Moving one date past the other drags the other along to the same day:
  // a single-day absence, instead of an end-before-start error.
  //
  // A typed date is the exception. It arrives one keystroke at a time and
  // every keystroke leaves a complete date behind, so typing "14" as the last
  // day passes through the 1st of the month. Dragging on that intermediate
  // value moves the start date to the 1st and nothing moves it back: the
  // planner types one date and silently keeps a longer absence than intended.
  // So a typed value that would invert the period is left alone until the
  // rest of it arrives — or until the field is left, which reports the same
  // date again without the typing mark and drags as before.
  const changeStartDate = (date: Date | null, origin?: DateChangeOrigin) => {
    const invertsPeriod =
      !!date && !!endDate && differenceInCalendarDays(endDate, date) < 0;
    if (invertsPeriod && origin?.typing) {
      return;
    }
    setStartDate(date);
    if (invertsPeriod) {
      setEndDate(date);
    }
  };
  const changeEndDate = (date: Date | null, origin?: DateChangeOrigin) => {
    const invertsPeriod =
      !!date && !!startDate && differenceInCalendarDays(startDate, date) > 0;
    if (invertsPeriod && origin?.typing) {
      return;
    }
    setEndDate(date);
    if (invertsPeriod) {
      setStartDate(date);
    }
  };

  const hasValidPeriod = !!startDate && !!endDate && endDate >= startDate;
  const endBeforeStart = !!startDate && !!endDate && endDate < startDate;
  // in calendar days, so a DST switch inside the period cannot swallow a day
  const dayCount =
    startDate && endDate && !endBeforeStart
      ? differenceInCalendarDays(endDate, startDate) + 1
      : 0;

  const dutyHourTypesQuery = useQuery(
    ['registerAbsenceSicknessDutyHourTypes', toDateString(startDate ?? today)],
    async () => {
      // the legacy endpoints expect full datetimes (yyyy-MM-dd'T'HH:mm:ss.SSS'Z')
      const { data } = await getRegisterAbsenceDutyHourTypes({
        startDate: toBackendDateTime(startDate ?? today),
        endDate: toBackendDateTime(endDate ?? startDate ?? today, true)
      });
      return (
        data
          .filter(
            (dutyHourType: PublicDutyHourType) =>
              dutyHourType.dccSvc === SICKNESS_DCC_SVC
          )

          // ziekte (ZK) always first as the default choice, the rest readable
          .sort((a, b) => {
            if (a.code === DEFAULT_DUTY_HOUR_TYPE_CODE) {
              return -1;
            }
            if (b.code === DEFAULT_DUTY_HOUR_TYPE_CODE) {
              return 1;
            }
            return a.description.localeCompare(b.description);
          })
      );
    },
    { enabled: open && hasValidPeriod, keepPreviousData: true }
  );

  // preselect when only one sickness type remains; in edit mode the loaded
  // absence dictates the type, so nothing may override it
  useEffect(() => {
    const types = dutyHourTypesQuery.data;
    if (!types?.length || isEdit) {
      return;
    }
    if (!types.some((type) => type.dutyTypeId === dutyTypeId)) {
      setDutyTypeId(types[0].dutyTypeId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dutyHourTypesQuery.data, isEdit]);

  const workAgreementsQuery = useQuery(
    [
      'registerAbsenceWorkAgreements',
      employee.id,
      toDateString(startDate ?? today),
      toDateString(endDate ?? startDate ?? today)
    ],
    async () =>
      (
        await getRegisterAbsenceDuties({
          employeeId: employee.id,
          startDate: toBackendDateTime(startDate ?? today),
          endDate: toBackendDateTime(endDate ?? startDate ?? today, true)
        })
      ).data.workAgreements,
    // edit mode brings its own work agreement along with the absence
    { enabled: open && hasValidPeriod && !isEdit, keepPreviousData: true }
  );

  const workAgreementRows = useMemo(
    () => workAgreementsQuery.data ?? [],
    [workAgreementsQuery.data]
  );

  // edit mode: the absence's own work agreement, as loaded with the absence
  const workAgreements = useMemo(
    () =>
      isEdit
        ? (absenceDetailsQuery.data?.workAgreements ?? []).filter(Boolean)
        : firstRowPerWorkAgreement(workAgreementRows),
    [isEdit, absenceDetailsQuery.data, workAgreementRows]
  );

  // every work agreement starts SELECTED, so all planned duties show right
  // away; unchecking one marks its duties as untouched in the consequences.
  // an explicit uncheck survives refetches (only unseen ids are seeded).
  // the rollout settings are seeded per work agreement with their defaults.
  useEffect(() => {
    setSelectedWaIds((current) => {
      const next = current.filter((id) =>
        workAgreements.some((workAgreement) => workAgreement.waId === id)
      );
      for (const workAgreement of workAgreements) {
        if (!seenWaIdsRef.current.has(workAgreement.waId)) {
          seenWaIdsRef.current.add(workAgreement.waId);
          next.push(workAgreement.waId);
        }
      }
      return next;
    });
    setWorkAgreementSettings((current) => {
      const next = { ...current };
      const recorded = getSettings();
      for (const workAgreement of workAgreements) {
        next[workAgreement.waId] =
          next[workAgreement.waId] ??
          createDefaultSetting(
            workAgreement,
            startDate,
            recorded[workAgreement.waId]
          );
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workAgreements]);

  const benefitChangeByWaId = useMemo(
    () => getBenefitChangeByWaId(workAgreementRows, startDate, endDate),
    [workAgreementRows, startDate, endDate]
  );

  const updateWorkAgreementSetting = (
    waId: number,
    patch: Partial<WorkAgreementSetting>
  ) =>
    setWorkAgreementSettings((current) => ({
      ...current,
      [waId]: {
        ...current[waId],
        ...patch,
        // a hand-picked rollout date stops following the absence period
        ...('rolloutStartDate' in patch
          ? { rolloutStartDateTouched: true }
          : {}),
        // an explicit (un)check stops the automatic opt-in below
        ...('useFteRollout' in patch ? { useFteRolloutTouched: true } : {})
      }
    }));

  // the rollout starts on the first day of the absence, so moving the period
  // moves it along (unless the planner picked a date themselves)
  useEffect(() => {
    if (!startDate) {
      return;
    }
    setWorkAgreementSettings((current) =>
      Object.fromEntries(
        Object.entries(current).map(([waId, setting]) => [
          waId,
          setting.rolloutStartDateTouched
            ? setting
            : { ...setting, rolloutStartDate: startDate }
        ])
      )
    );
  }, [startDate]);

  // the rollout is an explicit opt-in and only makes sense with a start date
  // AND at least one weekday; the backend chokes on a date without days
  // (unparsed-date cast), so both are sent together or not at all.
  // week hours only when filled.
  const toRolloutArgument = (workAgreement: RegisterAbsenceWorkAgreement) => {
    // PNIL never rolls out: always the no-rollout shape, and no week hours —
    // the backend would refuse both anyway
    if (isPnilContract(workAgreement)) {
      return {
        workAgreementId: workAgreement.waId,
        rolloutStartDate: null,
        inputDays: []
      };
    }
    const setting =
      workAgreementSettings[workAgreement.waId] ??
      createDefaultSetting(workAgreement, startDate);
    const hasRollout =
      setting.useFteRollout &&
      !!setting.rolloutStartDate &&
      setting.inputDays.length > 0;
    return {
      workAgreementId: workAgreement.waId,
      rolloutStartDate: hasRollout
        ? toBackendDateTime(setting.rolloutStartDate!)
        : null,
      inputDays: hasRollout ? setting.inputDays : [],
      ...(setting.inputWeekHours > 0
        ? { inputWeekHours: setting.inputWeekHours }
        : {})
    };
  };

  // debounce the rollout inputs so the consequences panel updates ~400ms
  // after the user stops changing dates/percentage/settings
  const rolloutInput = useMemo(
    () => ({
      employeeId: employee.id,
      dutyTypeId: typeof dutyTypeId === 'number' ? dutyTypeId : null,
      startDate: startDate ? toBackendDateTime(startDate) : null,
      endDate: endDate ? toBackendDateTime(endDate, true) : null,
      absencePercentage: percentage,
      workAgreementArguments: workAgreements.map(toRolloutArgument)
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      employee.id,
      dutyTypeId,
      startDate,
      endDate,
      percentage,
      workAgreements,
      workAgreementSettings
    ]
  );
  const [debouncedRolloutInput, setDebouncedRolloutInput] =
    useState(rolloutInput);

  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedRolloutInput(rolloutInput),
      ROLLOUT_DEBOUNCE_MS
    );
    return () => clearTimeout(timer);
  }, [rolloutInput]);

  const rolloutQuery = useQuery(
    ['registerAbsenceRollout', debouncedRolloutInput],
    async () => {
      const { data } = await getAbsenceRollout({
        employeeId: debouncedRolloutInput.employeeId,
        dutyTypeId: debouncedRolloutInput.dutyTypeId!,
        startDate: debouncedRolloutInput.startDate!,
        endDate: debouncedRolloutInput.endDate!,
        absencePercentage: debouncedRolloutInput.absencePercentage,
        workAgreementArguments: debouncedRolloutInput.workAgreementArguments
      });
      return data;
    },
    {
      enabled:
        open &&
        !isEdit &&
        hasValidPeriod &&
        debouncedRolloutInput.dutyTypeId !== null &&
        !!debouncedRolloutInput.startDate &&
        !!debouncedRolloutInput.endDate &&
        debouncedRolloutInput.workAgreementArguments.length > 0,
      keepPreviousData: true,
      // a refused rollout (e.g. missing CLA year record) is reported inside
      // the consequences panel; the app-wide error snackbar would repeat it
      // as a generic failure on every recalculation
      onError: () => {}
    }
  );

  const editDutyLines = useMemo(
    () =>
      isEdit
        ? toEditDutyLines({
            duties: absenceDetailsQuery.data?.duties ?? [],
            originalTypeDescription:
              dutyHourTypesQuery.data?.find(
                (type) => type.dutyTypeId === originalAbsence?.dutyTypeId
              )?.description ?? '',
            startDate,
            endDate,
            typeChanged:
              !!originalAbsence &&
              typeof dutyTypeId === 'number' &&
              dutyTypeId !== originalAbsence.dutyTypeId
          })
        : [],
    [
      isEdit,
      absenceDetailsQuery.data,
      dutyHourTypesQuery.data,
      startDate,
      endDate,
      dutyTypeId,
      originalAbsence
    ]
  );

  const existingDutyLines = useMemo(
    () => (isEdit ? editDutyLines : rolloutQuery.data?.existingDutyLines ?? []),
    [isEdit, editDutyLines, rolloutQuery.data]
  );
  const newDutyLines = useMemo(() => {
    const lines = isEdit ? [] : rolloutQuery.data?.fteRolloutDutyLines ?? [];
    if (!endDate) {
      return lines;
    }
    // the sickness ends on the expected last day: never create duties past it
    // (the backend rollout rolls out per whole week and spills past the end)
    const firstDayAfter = startOfDay(addDays(endDate, 1));
    return lines.filter((line) =>
      isBefore(parseBackendDateTime(line.startTime), firstDayAfter)
    );
  }, [isEdit, rolloutQuery.data, endDate]);

  const dutyCountByWaId = useMemo(
    () => countDutiesByWaId(existingDutyLines),
    [existingDutyLines]
  );

  const forSelectedWa = (line: AbsenceRolloutDutyLine) =>
    !!line.waId && selectedWaIds.includes(line.waId);
  // new duties are only created for selected work agreements; existing duty
  // lines of unselected work agreements stay visible as "untouched"
  const selectedNewDutyLines = newDutyLines.filter(forSelectedWa);

  // The rollout follows the period on its own: on when the work agreement has
  // no planned duties in it (so the planner sees duties-to-create instead of
  // "nothing happens"), and off again as soon as moving the dates brings a
  // planned duty into range — leaving it on there would create duties next to
  // the ones already planned. An explicit (un)check by the planner always wins.
  useEffect(() => {
    // `open` in the deps: the query data survives a close/reopen in the
    // react-query cache, so without it the opt-in would only happen once
    if (!open || !rolloutQuery.data) {
      return;
    }
    setWorkAgreementSettings((current) => {
      let changed = false;
      const next = { ...current };
      for (const workAgreement of workAgreements) {
        const setting = next[workAgreement.waId];
        if (!setting || setting.useFteRolloutTouched) {
          continue;
        }
        const hasDuties = existingDutyLines.some(
          (line) => line.waId === workAgreement.waId
        );
        // a zero-hours contract is never opted in for the planner: without
        // week hours the rollout would create nothing, and how many hours
        // this absence covers is a decision only the planner can make
        const shouldRollout =
          !isPnilContract(workAgreement) &&
          !isZeroHoursContract(workAgreement) &&
          !hasDuties &&
          setting.inputDays.length > 0;
        if (setting.useFteRollout !== shouldRollout) {
          next[workAgreement.waId] = {
            ...setting,
            useFteRollout: shouldRollout
          };
          changed = true;
        }
      }
      return changed ? next : current;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, rolloutQuery.data]);

  // a registered absence overlapping the period makes the save pointless: the
  // backend refuses overlapping absences per work agreement. Detected live
  // from the same source as the overview, so the planner hears it before
  // saving instead of after.
  // in edit mode the only overlap in reach is the absence being edited
  // itself (the period can only shrink), so the check stays off there
  const registeredAbsencesQuery = rapiniQueries.useGetAbsenceNotices(
    toDateString(startDate ?? today),
    toDateString(endDate ?? startDate ?? today),
    employee.id,
    { enabled: open && hasValidPeriod && !isEdit }
  );
  const overlappingAbsences = useMemo(
    () =>
      startDate && endDate
        ? (registeredAbsencesQuery.data ?? []).filter(
            (notice) =>
              notice.status === 'REGISTERED' &&
              isBefore(parseBackendDateTime(notice.start), endOfDay(endDate)) &&
              isAfter(parseBackendDateTime(notice.end), startOfDay(startDate))
          )
        : [],
    [registeredAbsencesQuery.data, startDate, endDate]
  );

  // wa numbers ("1" or "1, 2") of the overlapping absences; a selected work
  // agreement in that set means the backend will reject the whole save
  const overlappingWaNrs = useMemo(
    () =>
      new Set(
        overlappingAbsences.flatMap(
          (notice) =>
            notice.workAgreement
              ?.split(',')
              .map((nr) => nr.trim())
              .filter(Boolean) ?? []
        )
      ),
    [overlappingAbsences]
  );
  const hasBlockingOverlap = workAgreements.some(
    (workAgreement) =>
      selectedWaIds.includes(workAgreement.waId) &&
      overlappingWaNrs.has(String(workAgreement.nr))
  );

  const queryClient = useQueryClient();
  const saveMutation = useMutation(createAbsenceUpdateDuties, {
    onSuccess: () => {
      showSuccessSnackbar();
      onClose();
      queryClient.invalidateQueries('getAbsenceNotices');
      onSuccess?.();
    },
    onError: showError
  });
  const updateMutation = useMutation(updateAbsence, {
    onSuccess: () => {
      showSuccessSnackbar();
      onClose();
      queryClient.invalidateQueries('getAbsenceNotices');
      // the type change may partially fail per duty (backend logs and moves
      // on), so the next open must show the real state, not this cache
      queryClient.invalidateQueries(['registerAbsenceDetails', absenceId]);
      onSuccess?.();
    },
    onError: showError
  });

  // the backend only allows shrinking the period
  const startBeforeOriginal =
    isEdit &&
    !!startDate &&
    !!originalStartDate &&
    differenceInCalendarDays(startDate, originalStartDate) < 0;
  const endAfterOriginal =
    isEdit &&
    !!endDate &&
    !!originalEndDate &&
    differenceInCalendarDays(endDate, originalEndDate) > 0;

  /** do the working days differ from what was recorded for this work agreement? */
  const isWorkingDaysChanged = (waId: number) => {
    const setting = workAgreementSettings[waId];
    if (!setting) {
      return false;
    }
    const stored = getSettings()[waId]?.days;
    if (!stored) {
      return setting.inputDays.length > 0;
    }
    return [...stored].sort().join() !== [...setting.inputDays].sort().join();
  };

  /** store the current working days as the setting for next time */
  const saveWorkingDaysSetting = (waIds: number[]) =>
    saveSettings(
      Object.fromEntries(
        waIds.map((waId) => [
          waId,
          { days: workAgreementSettings[waId]?.inputDays ?? [] }
        ])
      )
    );

  const canSave =
    hasValidPeriod &&
    typeof dutyTypeId === 'number' &&
    (isEdit || selectedWaIds.length > 0) &&
    note.trim().length > 0 &&
    !hasBlockingOverlap &&
    !startBeforeOriginal &&
    !endAfterOriginal &&
    !(isEdit && !originalAbsence) &&
    !saveMutation.isLoading &&
    !updateMutation.isLoading;

  /** nothing changes in the planning: no duty of a selected work agreement is
   *  actually touched (SKIP means the backend leaves it as it is) and no new
   *  duties are created */
  const hasNoConsequences =
    !existingDutyLines.some(
      (line) =>
        !!line.waId &&
        selectedWaIds.includes(line.waId) &&
        line.action !== 'SKIP'
    ) && selectedNewDutyLines.length === 0;

  const save = () => {
    if (!canSave || !startDate || !endDate || typeof dutyTypeId !== 'number') {
      return;
    }
    const dutyHourType = dutyHourTypesQuery.data?.find(
      (type) => type.dutyTypeId === dutyTypeId
    );

    if (isEdit && originalAbsence) {
      // an unchanged date keeps the absence's original time of day: the
      // backend refuses anything outside the original window, and dojo-era
      // absences do not necessarily run 00:00-23:59
      const sameStartDay = isSameDay(
        startDate,
        parseBackendDateTime(originalAbsence.startTime)
      );
      const sameEndDay = isSameDay(
        endDate,
        parseBackendDateTime(originalAbsence.endTime)
      );
      updateMutation.mutate({
        absenceId: originalAbsence.absenceId,
        startTime: sameStartDay
          ? originalAbsence.startTime
          : toBackendDateTime(startDate),
        endTime: sameEndDay
          ? originalAbsence.endTime
          : toBackendDateTime(endDate, true),
        note,
        dutyTypeId,
        // derived like on create; null keeps the existing reason code
        absenceReasonCode:
          REASON_CODE_BY_DUTY_HOUR_TYPE_CODE[dutyHourType?.code ?? ''] ?? null
      });
      return;
    }

    saveMutation.mutate({
      startTime: toBackendDateTime(startDate),
      endTime: toBackendDateTime(endDate, true),
      dutyTypeId,
      dccSvc: dutyHourType?.dccSvc ?? 0,
      note,
      // derived, not asked: the aanvraagreden follows from the duty hour type;
      // the note carries the human-readable reason for the overview
      absenceReasonCode:
        REASON_CODE_BY_DUTY_HOUR_TYPE_CODE[dutyHourType?.code ?? ''] ?? null,
      // not created from a leave request; 0 would violate the FK constraint
      leaveRequestId: null,
      employeeId: employee.id,
      workAgreements: workAgreements
        .filter((workAgreement) => selectedWaIds.includes(workAgreement.waId))
        .map((workAgreement) => {
          const setting =
            workAgreementSettings[workAgreement.waId] ??
            createDefaultSetting(workAgreement, startDate);
          return {
            waId: workAgreement.waId,
            benefitId: workAgreement.benefitId,
            hoursWeek: setting.inputWeekHours,
            absPercentage: percentage,
            dutiesToUpdate: existingDutyLines
              .filter(
                (line) =>
                  line.action === 'MODIFY' &&
                  line.id &&
                  line.waId === workAgreement.waId
              )
              .map((line) => ({
                dutyLineId: line.id!,
                dutyDatetimeBegin: suffixZ(line.startTime),
                dutyDatetimeEnd: suffixZ(line.endTime)
              })),
            dutiesToDelete: existingDutyLines
              .filter(
                (line) =>
                  line.action === 'DELETE' &&
                  line.id &&
                  line.waId === workAgreement.waId
              )
              .map((line) => ({
                dutyLineId: line.id!,
                dutyDatetimeBegin: suffixZ(line.startTime),
                dutyDatetimeEnd: suffixZ(line.endTime)
              })),
            dutiesToCreate: newDutyLines
              .filter((line) => line.waId === workAgreement.waId)
              .map((line) => ({
                dutyDatetimeBegin: suffixZ(line.startTime),
                dutyDatetimeEnd: suffixZ(line.endTime)
              }))
          };
        })
    });
  };

  return {
    startDate,
    setStartDate: changeStartDate,
    endDate,
    setEndDate: changeEndDate,
    endBeforeStart,
    dayCount,
    dutyTypeId,
    setDutyTypeId,
    dutyHourTypes: dutyHourTypesQuery.data ?? [],
    percentage,
    setPercentage,
    note,
    setNote,
    workAgreements,
    selectedWaIds,
    setSelectedWaIds,
    workAgreementSettings,
    updateWorkAgreementSetting,
    dutyCountByWaId,
    benefitChangeByWaId,
    existingDutyLines,
    selectedNewDutyLines,
    isRolloutLoading: rolloutQuery.isLoading || rolloutQuery.isFetching,
    // the panel may only judge "nothing happens" on an actual result, never
    // on the emptiness before the first calculation arrived
    hasConsequencesResult: isEdit
      ? !!absenceDetailsQuery.data
      : !!rolloutQuery.data,
    // backend key of a refused rollout (e.g. missing CLA configuration), so
    // the panel can say why instead of claiming nothing changes
    rolloutErrorKey: (
      rolloutQuery.error as {
        response?: { data?: { error?: { message?: string } } };
      } | null
    )?.response?.data?.error?.message,
    hasNoConsequences,
    overlappingAbsences,
    hasBlockingOverlap,
    // whether detaching a planned duty gives its request back as an open duty
    detachedGivesOpenDuty: !!dutyHourTypesQuery.data
      ?.find((type) => type.dutyTypeId === dutyTypeId)
      ?.flags?.includes('REQ_DISCONNECT_AUTO'),
    isWorkingDaysChanged,
    saveWorkingDaysSetting,
    canSave,
    isSaving: saveMutation.isLoading || updateMutation.isLoading,
    save,
    // edit mode
    isEdit,
    isLoadingAbsence: isEdit && absenceDetailsQuery.isLoading,
    originalAbsence,
    originalStartDate,
    originalEndDate,
    startBeforeOriginal,
    endAfterOriginal
  };
};
